/* eslint-disable eqeqeq */
import React, { useState, useEffect } from 'react';
import '../../styles/Modal.css';
import useWindowWidth from '../../customHook/useWindowWidth';

/**
 * Компонент модального окна с динамической шириной и высотой, рассчитанной относительно контейнера.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Открыто ли модальное окно.
 * @param {Function} [props.onClose] - Обработчик закрытия окна.
 * @param {string} [props.containerSelector] - Селектор контейнера, от которого зависит ширина.
 * @param {JSX.Element} props.children - Контент внутри модального окна.
 * @param {string|number} [props.width] - Фиксированная ширина окна (если передана).
 * @param {string|number} [props.height] - Фиксированная высота окна (если передана).
 * @param {string|number} [props.minusSize] - Значение, которое нужно вычесть из ширины контейнера (px или CSS var).
 * @param {string|number} [props.top] - Смещение сверху, если позиционируется по центру.
 */
function Modal({
  isOpen,
  onClose,
  containerSelector,
  children,
  width: propWidth,
  height: propHeight,
  minusSize,
  top,
  margin,
}) {
  const [containerWidth, setContainerWidth] = useState(null);
  const [containerHeight, setContainerHeight] = useState(null);
  const [isReady, setIsReady] = useState(false); // Флаг готовности модалки к показу
  const { width: windowWidth } = useWindowWidth(); // Получаем ширину окна браузера

  /**
   * При открытии модального окна вычисляет ширину и высоту окна.
   * Ширина может зависеть от родительского контейнера, из которого вычитается `minusSize`.
   */
  useEffect(() => {
    if (!isOpen) return;

    const frame = requestAnimationFrame(() => {
      const container = containerSelector ? document.querySelector(containerSelector) : null;

      /**
       * Преобразует значение CSS-переменной или строки в пиксели.
       * @param {string|number} variable - CSS-переменная или число.
       * @returns {number} - Значение в пикселях.
       */
      const getCSSVariableValueInPixels = (variable) => {
        if (!variable) return 0;
        if (typeof variable === 'string' && variable.startsWith('--')) {
          const computedStyle = getComputedStyle(document.documentElement);
          const value = computedStyle.getPropertyValue(variable).trim();
          if (!value) return 0;

          const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
          if (value.includes('rem')) return parseFloat(value) * rootFontSize;
          return parseFloat(value) || 0;
        }
        return parseFloat(variable) || 0;
      };

      const minusValue = getCSSVariableValueInPixels(minusSize);

      // Вычисляем размеры модального окна
      if (container) {
        const width = container.offsetWidth - minusValue;
        setContainerWidth(propWidth || width);
        setContainerHeight(propHeight || null);
      } else {
        setContainerWidth(propWidth || '90%');
        setContainerHeight(propHeight || null);
      }

      // Устанавливаем готовность к отображению
      setIsReady(true);
    });

    // Очистка эффекта
    return () => {
      cancelAnimationFrame(frame);
      setIsReady(false); // Сбрасываем флаг готовности при закрытии
    };
  }, [isOpen, containerSelector, windowWidth, propWidth, propHeight, minusSize]);

  /**
   * Стили модального окна, если заданы фиксированные размеры.
   */
  const modalStyle = {
    width: containerWidth,
    height: containerHeight,
    ...(propWidth &&
      propHeight && {
        position: 'fixed',
        top: top || '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        margin: 0,
      }),
  };

  /**
   * Блокирует прокрутку фона при открытии модального окна.
   */
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [isOpen]);

  return (
    <div className="modal-overlay" style={{ display: isOpen && isReady ? 'block' : 'none' }}>
      <div className="modal-content" style={modalStyle}>
        {onClose && (
          <button
            id="close_modal_button"
            className="close-button"
            onClick={onClose}
            style={{ display: isOpen ? 'block' : 'none' }}
          >
            <span className="close-icon" aria-hidden="true">
              &times;
            </span>
          </button>
        )}
        {children}
      </div>
    </div>
  );
}

/**
 * Обертка, добавляющая глобальный слушатель ESC для закрытия последней открытой модалки.
 *
 * @param {Object} props
 * @param {JSX.Element} props.children - Контент, внутри которого слушается ESC.
 */
export function ModalEscListener({ children }) {
  useEffect(() => {
    function handler(ev) {
      if (ev.key != 'Escape') return;

      // Ищем последнюю видимую кнопку закрытия
      const _buttons = document.querySelectorAll('#close_modal_button');
      const buttons = Array.from(_buttons).reverse();
      for (let button of buttons) {
        if (button.checkVisibility?.({ checkOpacity: true, checkVisibilityCSS: true })) {
          button.click();
          break;
        }
      }
    }

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return children;
}

export default Modal;
